import { TimelineMax, TweenMax, Power2, Power3, Power4 } from "gsap"
import { vec3, quat } from 'gl-matrix'

import when from 'when'

import TensorManager from "@/tensor/tensorManager"
import WebsocketManager from '@/js/websocketManager.js'

import DebugController from '@/glxp/debug/debugController'

import DebugPoint from "../debug/point"
import Easing from "@/js/utils/easing"
import Node from '../abstract/node'


const _VEC31 = vec3.create()
const _VEC32 = vec3.create()
const _QUAT1 = quat.create()
const _QUAT2 = quat.create()

const POSE_ROTATIONS_X = { "rock": 0, "idle-01": 0, "paper": 0, "scisors": 0 }
const POSE_ROTATIONS_Y = { "rock": 0, "idle-01": 0, "paper": 0, "scisors": 0 }
const POSE_ROTATIONS_Z = { "rock": 0, "idle-01": 0, "paper": 0, "scisors": 0 }


let debugId = 0

class HandAnimator {
    constructor(scene, nodeTree, rootNode, mesh){
        this.scene              = scene
        this.nodeTree           = nodeTree
        this.rootNode           = rootNode
        this.mesh               = mesh
        this.animations         = {}

        this.clock              = setTimeout(() => {}, 0)
        this.tracked            = false
        this.trackingEnabled    = true
        this.websocketEnabled   = false
        this.playShifumiAnim    = false
        this.sign               = "none"
        this.lastSign           = "none"

        this.currentAnim        = null
        this.pendingAnim        = null
        this.animationBlending  = 0
        this.positionOffset     = { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0}

        this.initTree()

        TensorManager.on("signFound", (sign)=>{ this.onSign(sign) })
        WebsocketManager.on("tracking", this.onWebsocketData.bind(this))

        this.config = {
            rx: { value: 0, range: [-3, 3] },
            ry: { value: 0, range: [-3, 3] },
            rz: { value: 0, range: [-3, 3] },
        }
        DebugController.addConfig(this.config, 'Hands-Animator-' + debugId)
        debugId++

    }

    setAnim(anim){
        this.currentAnim = anim
    }

    onLoaded(){
        for (const key in this.animations) {
            if (this.animations.hasOwnProperty(key)) {
                for (let i = 0; i < this.animations[key].length; i++) {
                    const anim = this.animations[key][i];
                    anim.time = 0
                    anim.frame = 0
                    anim.node = new Node()
                    anim.node.name = anim.target
                    
                    let tmpMaxFrame = 0
                    for (const key in anim.animations) {
                        if (anim.animations.hasOwnProperty(key)) {
                            const element = anim.animations[key];
                            if (element.frameLength > tmpMaxFrame) {
                                tmpMaxFrame = element.frameLength
                            }
                        }
                    }
                    anim.frameLength = tmpMaxFrame
                }
            }
        }

        this.shifumiAnim = this.animations.shifumi[2]
    }

    onWebsocketData(hand){
        
    }

    onSign(sign){
        this.sign = sign
    }

    initTree(){

        this.nodesDictionary = {}
        for (let i = 0; i < this.nodeTree.length; i++) {
            this.nodeTree[i].initialPos = vec3.clone(this.nodeTree[i].position)
            this.nodeTree[i].initialRot = vec3.clone(this.nodeTree[i].rotation)
            this.nodeTree[i].forceUpdate = true
            this.nodesDictionary[this.nodeTree[i].name] = this.nodeTree[i]
            if (this.nodeTree[i].name !== "HandRig") {
                this.nodeTree[i].manualRot = true
            }
        }
        this.rootNode.forceUpdate = true

        this.tree = {}
        this.tree.thumb = [ this.getNode("thumb0"), this.getNode("thumb1"), this.getNode("thumb2"), this.getNode("thumb3") ]
        this.tree.indexFinger = [ this.getNode("indexFinger0"), this.getNode("indexFinger1"), this.getNode("indexFinger2"), this.getNode("indexFinger3") ]
        this.tree.middleFinger = [ this.getNode("middleFinger0"), this.getNode("middleFinger1"), this.getNode("middleFinger2"), this.getNode("middleFinger3") ]
        this.tree.ringFinger = [ this.getNode("ringFinger0"), this.getNode("ringFinger1"), this.getNode("ringFinger2"), this.getNode("ringFinger3") ]
        this.tree.pinky = [ this.getNode("pinky0"), this.getNode("pinky1"), this.getNode("pinky2"), this.getNode("pinky3") ]

    }

    getNode(name) {
        for (let i = 0; i < this.nodeTree.length; i++) {
            if (this.nodeTree[i].name == name) {
                return this.nodeTree[i]
            }
        }
    }

    playShifumiAnimation(){
        this.goToState("rock")
        this.playShifumiAnim = true
    }

    updateAnimationTrack(id){
        for (let i = 0; i < this.animations[id].length; i++) {
            const anim = this.animations[id][i];
            if (anim.frameLength > 0) {
                anim.time += this.scene.dt 
                anim.frame ++
                anim.frame = anim.frame % anim.frameLength
            }
        }
    }

    getPosesFromTrack(id){
        let nodes = []
        for (let i = 0; i < this.animations[id].length; i++) {
            const anim = this.animations[id][i];
            if (anim.frameLength === 0) {
                continue
            }
            anim.node.rotation[0] = (anim.animations["rotateX"].source[anim.frame] / 180) * Math.PI
            anim.node.rotation[1] = (anim.animations["rotateY"].source[anim.frame] / 180) * Math.PI
            anim.node.rotation[2] = (anim.animations["rotateZ"].source[anim.frame] / 180) * Math.PI
            nodes.push(anim.node)
        }
        return nodes
    }

    applyAnimationFromTracks(id1, id2, blend){
        let poses1 = this.getPosesFromTrack(id1)
        let poses2
        if (id2 !== null) {
            poses2 = this.getPosesFromTrack(id2)
        }

        for (let i = 0; i < poses1.length; i++) {
            const n1 = poses1[i];
            const fNode = this.nodesDictionary[n1.name]
            
            if (fNode == undefined) {
                continue
            }
            
            quat.identity(_QUAT1)
            quat.rotateY(_QUAT1, _QUAT1, n1.rotation[1])
            quat.rotateX(_QUAT1, _QUAT1, n1.rotation[0])
            quat.rotateZ(_QUAT1, _QUAT1, n1.rotation[2])
            
            if (id2 !== null) {
                const n2 = poses2[i];
                quat.identity(_QUAT2)
                quat.rotateY(_QUAT2, _QUAT2, n2.rotation[1])
                quat.rotateX(_QUAT2, _QUAT2, n2.rotation[0])
                quat.rotateZ(_QUAT2, _QUAT2, n2.rotation[2])
            }
            
            quat.slerp(fNode.quaternion, _QUAT1, _QUAT2, blend)
            
        }
    }

    goToState(name){

        if (this.currentAnim == name) {
            return
        }

        this.pendingAnim = name
        TweenMax.killTweensOf(this)
        TweenMax.to(this, {
            duration: name.includes("idle") ? 3 : .75,
            animationBlending: 1,
            ease: "expo.inOut",
            onComplete:()=>{
                this.pendingAnim = null
                this.animationBlending = 0
                this.currentAnim = name
            }
        })

        TweenMax.killTweensOf(this.positionOffset)
        TweenMax.to(this.positionOffset,
        {
            duration: 1.5,
            rx: POSE_ROTATIONS_X[name],
            ry: POSE_ROTATIONS_Y[name],
            rz: POSE_ROTATIONS_Z[name],
            ease: "power2.inOut"
        })

        // if (name.includes("idle") == false) {
        //     TweenMax.fromTo(this.positionOffset,
        //     { 
        //         y: Math.random() * .3,
        //         ry: (Math.random()-.5) * 2 * Math.random() * .3,
        //     },
        //     {
        //         duration: .75,
        //         y: 0,
        //         ry: 0 + POSE_ROTATIONS_X[name],
        //         ease: "elastic.out(1, 0.3)",
        //     })
        // }

    }

    update(){

        if (this.currentAnim == null) {
            return
        }

        this.updateAnimationTrack(this.currentAnim)
        if (this.pendingAnim !== null) {
            this.updateAnimationTrack(this.pendingAnim)
        }
        
        if (this.playShifumiAnim == true){
            this.updateAnimationTrack("shifumi")
            if (this.shifumiAnim.frame > (this.shifumiAnim.frameLength - 2)) {
                this.playShifumiAnim = false
                this.shifumiAnim.frame = 0
            }
        }

        this.applyAnimationFromTracks(this.currentAnim, this.pendingAnim, this.animationBlending)

        let rootRigNode = this.getNode("HandRig")
        rootRigNode.rotation[0] = Math.sin(this.scene.time) * .05 + this.positionOffset.rx + this.config.rx.value + (this.shifumiAnim.animations.rotateX.source[this.shifumiAnim.frame] / 180) * Math.PI
        rootRigNode.rotation[1] = Math.cos(this.scene.time + 2.394723) * .15 - (Math.PI * .85) + this.positionOffset.ry + this.config.ry.value + (this.shifumiAnim.animations.rotateY.source[this.shifumiAnim.frame] / 180) * Math.PI
        rootRigNode.rotation[2] = Math.cos(this.scene.time + 1.347344) * .05 + this.positionOffset.rz + this.config.rz.value + (this.shifumiAnim.animations.rotateZ.source[this.shifumiAnim.frame] / 180) * Math.PI

        rootRigNode.position[0] = Math.cos((this.scene.time * .6) + 5.238744) * 1 + rootRigNode.initialPos[0] + this.positionOffset.x + this.shifumiAnim.animations.translate.source[this.shifumiAnim.frame * 3 + 0]
        rootRigNode.position[1] = Math.sin((this.scene.time * .6) + 8.328748) * 1 + rootRigNode.initialPos[1] - 3 + this.positionOffset.y + this.shifumiAnim.animations.translate.source[this.shifumiAnim.frame * 3 + 1]
        rootRigNode.position[2] = Math.cos((this.scene.time * .6) + 2.394723) * 2 + rootRigNode.initialPos[2] + this.positionOffset.z + this.shifumiAnim.animations.translate.source[this.shifumiAnim.frame * 3 + 2]

    }


}

export default HandAnimator